import React, { useState } from 'react';
import { ConfigProvider, Tag } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import Modal from '../../../atoms/Modal/Modal';
import Tabs from '../../../atoms/Tabs/Tabs';
import Input from '../../../atoms/Input/Input';
import Button from '../../../atoms/Button/Button';
import { CreateCaseModalProps, CaseItem, CaseCategory } from './CreateCaseModal.types';

/**
 * Briefcase Icon Component
 */
const BriefcaseIcon: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = '#7C3AED' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M8 21V7C8 5.89543 8.89543 5 10 5H14C15.1046 5 16 5.89543 16 7V21M4 9H20C20.5523 9 21 9.44772 21 10V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V10C3 9.44772 3.44772 9 4 9Z"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Category Tag Component
 */
const CategoryTag: React.FC<{ category: CaseCategory }> = ({ category }) => {
    const getCategoryConfig = () => {
        switch (category.type) {
            case 'Promo':
                return {
                    bgColor: '#FFF7E6',
                    color: '#D46B08',
                    icon: (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                            <path d="M8 12L16 12M12 8L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    ),
                };
            case 'PPA':
                return {
                    bgColor: '#F0E9FF',
                    color: '#7C3AED',
                    icon: (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M12 2L3 7L3 10C3 15.55 6.16 20.74 11 22C15.84 20.74 19 15.55 19 10L19 7L12 2Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ),
                };
            case '$ Pricing':
                return {
                    bgColor: '#FFF0F6',
                    color: '#C41D7F',
                    icon: (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M12 6V18M15 9C15 10.6569 13.6569 12 12 12C10.3431 12 9 10.6569 9 9C9 7.34315 10.3431 6 12 6C13.6569 6 15 7.34315 15 9Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ),
                };
            case 'Growth':
                return {
                    bgColor: '#E6F7FF',
                    color: '#0958D9',
                    icon: (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M3 12L7 8L12 13L16 9L21 14M21 14V10M21 14H17"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ),
                };
            default:
                return {
                    bgColor: '#F5F5F5',
                    color: '#595959',
                    icon: null,
                };
        }
    };

    const config = getCategoryConfig();

    return (
        <Tag
            style={{
                backgroundColor: config.bgColor,
                color: config.color,
                border: 'none',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '12px',
                lineHeight: '20px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontFamily: "'Open Sans', sans-serif",
            }}
        >
            {config.icon && <span>{config.icon}</span>}
            <span>{category.type}</span>
        </Tag>
    );
};

/**
 * Case Card Component for existing cases list
 */
const CaseCard: React.FC<{
    case: CaseItem;
    isSelected: boolean;
    onSelect: () => void;
    'data-test-id'?: string;
}> = ({ case: caseItem, isSelected, onSelect, 'data-test-id': dataTestId }) => {
    return (
        <div
            onClick={onSelect}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                isSelected
                    ? 'border-[#7C3AED] bg-[#F5F3FF]'
                    : 'border-[rgba(0,0,0,0.06)] bg-white hover:border-[rgba(0,0,0,0.15)] hover:bg-[#FAFAFA]'
            }`}
            data-test-id={dataTestId}
        >
            {/* Briefcase Icon */}
            <div className="flex-shrink-0 mt-1">
                <BriefcaseIcon size={20} color={isSelected ? '#7C3AED' : '#8B8B8B'} />
            </div>

            {/* Case Content */}
            <div className="flex-1 min-w-0">
                <div className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] mb-1">
                    {caseItem.title}
                </div>
                <div className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] mb-2">
                    {caseItem.description || 'Minor description on the case written here.'}
                </div>
            </div>

            {/* Category Tag */}
            {caseItem.category && (
                <div className="flex-shrink-0">
                    <CategoryTag category={caseItem.category} />
                </div>
            )}
        </div>
    );
};

/**
 * CreateCaseModal - Modal for creating a new case or adding to an existing case
 * Part of the AskAsper Agentic Platform components
 */
const CreateCaseModal: React.FC<CreateCaseModalProps> = ({
    open,
    onCancel,
    onSave,
    existingCases = [],
    defaultTab = 'existing',
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState<string>(defaultTab);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
    const [caseName, setCaseName] = useState<string>('');
    const [caseDescription, setCaseDescription] = useState<string>('');

    // Filter cases based on search query
    const filteredCases = existingCases.filter((caseItem) =>
        caseItem.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        caseItem.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Handle save
    const handleSave = () => {
        if (activeTab === 'existing') {
            if (selectedCaseId) {
                const selectedCase = existingCases.find((c) => c.id === selectedCaseId);
                onSave?.({
                    type: 'existing',
                    caseId: selectedCaseId,
                    case: selectedCase,
                });
            }
        } else {
            if (caseName.trim()) {
                onSave?.({
                    type: 'new',
                    caseName: caseName.trim(),
                    caseDescription: caseDescription.trim() || undefined,
                });
            }
        }
    };

    // Check if save button should be enabled
    const isSaveEnabled = () => {
        if (activeTab === 'existing') {
            return selectedCaseId !== null;
        } else {
            return caseName.trim().length > 0;
        }
    };

    // Reset form when modal closes
    React.useEffect(() => {
        if (!open) {
            setSearchQuery('');
            setSelectedCaseId(null);
            setCaseName('');
            setCaseDescription('');
            setActiveTab(defaultTab);
        }
    }, [open, defaultTab]);

    const tabItems = [
        {
            key: 'new',
            label: (
                <span className="font-['Open_Sans'] font-medium text-[14px] leading-[22px]">Create New Case</span>
            ),
            children: (
                <div className="space-y-4 pt-4">
                    {/* Case Name Input */}
                    <div>
                        <label className="block font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] mb-2">
                            Case Name<span className="text-red-500 ml-1">*</span>
                        </label>
                        <Input
                            placeholder="Eg: Analysis on NSV"
                            value={caseName}
                            onChange={(e) => setCaseName(e.target.value)}
                            data-test-id={`${dataTestId}-case-name-input`}
                        />
                    </div>

                    {/* Description Textarea */}
                    <div>
                        <label className="block font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] mb-2">
                            Description <span className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.45)]">(Optional)</span>
                        </label>
                        <Input.TextArea
                            placeholder="Describe your case"
                            value={caseDescription}
                            onChange={(e) => setCaseDescription(e.target.value)}
                            rows={4}
                            data-test-id={`${dataTestId}-case-description-textarea`}
                        />
                    </div>
                </div>
            ),
        },
        {
            key: 'existing',
            label: (
                <span className="font-['Open_Sans'] font-medium text-[14px] leading-[22px]">Add to existing</span>
            ),
            children: (
                <div className="space-y-4 pt-4">
                    {/* Search Bar */}
                    <Input.Search
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        allowClear
                        data-test-id={`${dataTestId}-search-input`}
                    />

                    {/* Cases List */}
                    <div className="max-h-[400px] overflow-y-auto space-y-2">
                        {filteredCases.length > 0 ? (
                            filteredCases.map((caseItem) => (
                                <CaseCard
                                    key={caseItem.id}
                                    case={caseItem}
                                    isSelected={selectedCaseId === caseItem.id}
                                    onSelect={() => setSelectedCaseId(caseItem.id)}
                                    data-test-id={`${dataTestId}-case-card-${caseItem.id}`}
                                />
                            ))
                        ) : (
                            <div className="text-center py-8 text-[rgba(0,0,0,0.45)] font-['Open_Sans'] font-normal text-[14px] leading-[22px]">
                                {searchQuery ? 'No cases found matching your search.' : 'No existing cases available.'}
                            </div>
                        )}
                    </div>
                </div>
            ),
        },
    ];

    return (
        <ConfigProvider theme={theme}>
            <style>{`
                .create-case-tabs .ant-tabs-tab {
                    padding: 8px 16px !important;
                    margin: 0 !important;
                    border-radius: 6px 6px 0 0 !important;
                    background: white !important;
                    border: none !important;
                    font-family: 'Open Sans', sans-serif !important;
                }
                .create-case-tabs .ant-tabs-tab-active {
                    background: #F0E9FF !important;
                }
                .create-case-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
                    color: #7C3AED !important;
                    font-weight: 500 !important;
                }
                .create-case-tabs .ant-tabs-tab:not(.ant-tabs-tab-active) .ant-tabs-tab-btn {
                    color: rgba(0, 0, 0, 0.65) !important;
                }
                .create-case-tabs .ant-tabs-nav {
                    margin-bottom: 16px !important;
                }
                .create-case-tabs .ant-tabs-nav::before {
                    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
                }
                .create-case-tabs .ant-tabs-ink-bar {
                    display: none !important;
                }
            `}</style>
            <Modal
                open={open}
                onCancel={onCancel}
                title={
                    <span className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)]">
                        Create Case
                    </span>
                }
                footer={null}
                width={600}
                centered
                closable
                maskClosable={true}
                data-test={dataTest}
                data-test-id={dataTestId}
            >
                <div className="pb-4">
                    {/* Tabs */}
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems}
                        className="create-case-tabs"
                        data-test-id={`${dataTestId}-tabs`}
                    />

                    {/* Footer Buttons */}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[rgba(0,0,0,0.06)]">
                        <Button
                            onClick={onCancel}
                            className="h-9 px-4 font-['Open_Sans'] font-normal text-[14px] leading-[22px]"
                            data-test-id={`${dataTestId}-cancel-button`}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSave}
                            disabled={!isSaveEnabled()}
                            className="h-9 px-4 bg-[#1f1f1f] border-[#1f1f1f] hover:bg-[#333] hover:border-[#333] font-['Open_Sans'] font-normal text-[14px] leading-[22px]"
                            data-test-id={`${dataTestId}-save-button`}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>
        </ConfigProvider>
    );
};

export default CreateCaseModal;
